"use client";

import type { ReactNode } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import type { Control, FieldArray, FieldArrayPath, FieldValues } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { cn } from "@/lib/cn";

type RepeatingGroupProps<TFieldValues extends FieldValues, TFieldArrayName extends FieldArrayPath<TFieldValues>> = {
  className?: string;
  control: Control<TFieldValues>;
  name: TFieldArrayName;
  addLabel: string;
  /** Shown instead of `addLabel` while the group is empty, where "add another" would read wrong. */
  emptyLabel?: string;
  minEntries?: number;
  maxEntries?: number;
  /** The rule between entries. Off where the design stacks rows with nothing but space. */
  separators?: boolean;
  /** Read mode: the entries still render, but nothing can be added or removed. */
  disabled?: boolean;
  defaultItem: () => FieldArray<TFieldValues, TFieldArrayName>;
  renderItem: (index: number) => ReactNode;
};

export function RepeatingGroup<TFieldValues extends FieldValues, TFieldArrayName extends FieldArrayPath<TFieldValues>>({
  className,
  control,
  name,
  addLabel,
  emptyLabel,
  minEntries = 0,
  maxEntries,
  separators = true,
  disabled,
  defaultItem,
  renderItem,
}: RepeatingGroupProps<TFieldValues, TFieldArrayName>) {
  const { fields, append, remove } = useFieldArray({ control, name });
  const canRemove = !disabled && fields.length > minEntries;
  const canAdd = maxEntries === undefined || fields.length < maxEntries;

  return (
    <div className={cn("flex flex-col gap-32", className)}>
      {fields.map((field, index) => (
        <div key={field.id} className="flex flex-col gap-32">
          {separators && index > 0 && <hr className="border-neutral-light-grey" />}
          <div className="flex items-start gap-16">
            <div className="min-w-0 flex-1">{renderItem(index)}</div>
            {canRemove && (
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label="Remove entry"
                className="shrink-0 cursor-pointer rounded-small p-8 text-neutral-grey hover:bg-neutral-black/5 hover:text-brand-red"
              >
                <TrashIcon className="size-20" />
              </button>
            )}
          </div>
        </div>
      ))}
      {canAdd && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => append(defaultItem())}
          className={cn(
            "inline-flex w-fit items-center gap-8 text-16",
            disabled
              ? "cursor-not-allowed font-medium text-neutral-grey"
              : "cursor-pointer font-semibold text-brand-dark-blue hover:underline",
          )}
        >
          <PlusIcon className="size-20" />
          {fields.length === 0 ? (emptyLabel ?? addLabel) : addLabel}
        </button>
      )}
    </div>
  );
}
